import { useCallback } from 'react'

import type { SortDescriptor } from '@heroui/react'
import { Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Pagination } from '@/components/ui/Pagination'
import { RowsPerPage } from '@/components/ui/RowsPerPage'
import { usePaymentsStore } from '@/store/payments.store'

import { SettlementsTableRow } from './SettlementsTableRow'

interface SettlementsTableProps {
  sortDescriptor: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
}

export function SettlementsTable({
  sortDescriptor,
  onSortChange,
}: SettlementsTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { settlements, pagination, setPagination } = usePaymentsStore()

  const handlePageChange = useCallback((pageNumber: number) => {
    setPagination({ page: pageNumber })
  }, [setPagination])

  const handleLimitChange = useCallback((limit: number) => {
    setPagination({ limit, page: 0 })
  }, [setPagination])

  const handleSelectDriver = useCallback((id: string) => {
    navigate(`/settlements/${id}`)
  }, [navigate])

  const start = pagination.total === 0 ? 0 : pagination.page * pagination.limit + 1
  const end = Math.min((pagination.page + 1) * pagination.limit, pagination.total)

  return (
    <Table className="min-h-[200px]">
      <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.72))]">
        <Table.Content
          aria-label="Settlements drivers list table"
          sortDescriptor={sortDescriptor}
          onSortChange={onSortChange}
        >
          <Table.Header className="sticky top-0 z-10">
            <Table.Column allowsSorting id="name">
              {t('drivers.full_name')}
            </Table.Column>
            <Table.Column allowsSorting id="phone">
              {t('drivers.phone')}
            </Table.Column>
            <Table.Column id="vehicle">
              {t('vehicles.title')}
            </Table.Column>
            <Table.Column id="balance" className="text-right">
              {t('settlements.balance')}
            </Table.Column>
            <Table.Column></Table.Column>
          </Table.Header>
          <Table.Body>
            {settlements.map(s => (
              <SettlementsTableRow
                key={s.Driver.ID}
                settlement={s}
                onSelect={handleSelectDriver}
              />
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
      <Table.Footer>
        <div className="flex w-full items-center justify-between ">
          <div className="flex items-center gap-4">
            <RowsPerPage
              value={pagination.limit}
              onChange={handleLimitChange}
            />
            <span className="text-xs text-foreground/50">
              {t('common.pagination_summary', { start, end, total: pagination.total })}
            </span>
          </div>
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={handlePageChange}
          />
        </div>
      </Table.Footer>
    </Table>
  )
}
